import {
  UseCaseParticipationRequest,
  UseCaseParticipationComment
} from '../../../services/use-case-participation/use-case-participation.model';
import { AdmissionState, CommentType } from '../../../services/organizations/organization.model';

const STORAGE_KEY = 'htx-mock-use-case-participations';
const STATE_CHANGES_KEY = 'htx-mock-use-case-participation-state-changes';

let additionalRequests: UseCaseParticipationRequest[] = [];
let stateChanges: Map<string, AdmissionState> = new Map();

function loadPersistedData(): void {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    additionalRequests = JSON.parse(stored);
  }
  const changes = localStorage.getItem(STATE_CHANGES_KEY);
  if (changes) {
    stateChanges = new Map(JSON.parse(changes));
  }
}

function persistAdditionalRequests(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(additionalRequests));
}

function persistStateChanges(): void {
  localStorage.setItem(STATE_CHANGES_KEY, JSON.stringify([...stateChanges]));
}

loadPersistedData();

const MOCK_USE_CASE_REQUESTS: UseCaseParticipationRequest[] = [
  {
    id: 'ucr-1',
    organizationId: 'org-456',
    useCaseId: 'supply-chain',
    useCaseName: 'Supply Chain Digitalization',
    organizationLegalName: 'Innovatech Corporation',
    representativeName: 'Maria Schmidt',
    admissionState: AdmissionState.PENDING,
    createdDate: '2026-03-15T10:30:00Z'
  },
  {
    id: 'ucr-2',
    organizationId: 'org-234',
    useCaseId: 'shortage-management',
    useCaseName: 'Shortage Management',
    organizationLegalName: 'AutoTech Innovations AG',
    representativeName: 'Thomas Müller',
    admissionState: AdmissionState.PENDING,
    createdDate: '2026-03-20T14:15:00Z'
  },
  {
    id: 'ucr-3',
    organizationId: 'org-123',
    useCaseId: 'supply-chain',
    useCaseName: 'Supply Chain Digitalization',
    organizationLegalName: 'Tech Solutions Limited',
    representativeName: 'Alice Johnson',
    admissionState: AdmissionState.APPROVED,
    createdDate: '2026-02-10T09:00:00Z'
  },
  {
    id: 'ucr-4',
    organizationId: 'org-789',
    useCaseId: 'shortage-management',
    useCaseName: 'Shortage Management',
    organizationLegalName: 'Green Energy Solutions GmbH',
    representativeName: 'Anna Weber',
    admissionState: AdmissionState.REJECTED,
    createdDate: '2026-02-25T16:45:00Z'
  },
  {
    id: 'ucr-5',
    organizationId: 'org-567',
    useCaseId: 'supply-chain',
    useCaseName: 'Supply Chain Digitalization',
    organizationLegalName: 'BioMed Research KG',
    representativeName: 'Laura Fischer',
    admissionState: AdmissionState.PENDING,
    createdDate: '2026-04-01T08:20:00Z'
  }
];

const MOCK_USE_CASE_COMMENTS: Record<string, UseCaseParticipationComment[]> = {
  'ucr-1': [
    {
      id: 'ucc-1',
      text: 'Antrag auf Teilnahme am Supply Chain Use Case eingegangen. Prüfung der Voraussetzungen steht aus.',
      date: '2026-03-15T10:30:00Z',
      author: 'System',
      type: CommentType.COMMENT
    }
  ],
  'ucr-2': [],
  'ucr-3': [
    {
      id: 'ucc-2',
      text: 'Alle technischen Voraussetzungen erfüllt. Organisation ist bereits aktiv im Dataspace.',
      date: '2026-02-10T09:00:00Z',
      author: 'Admin User',
      type: CommentType.COMMENT
    },
    {
      id: 'ucc-3',
      text: 'Teilnahme genehmigt. Organisation erfüllt alle Anforderungen für den Supply Chain Use Case.',
      date: '2026-02-12T11:30:00Z',
      author: 'Admin User',
      type: CommentType.APPROVAL
    }
  ],
  'ucr-4': [
    {
      id: 'ucc-4',
      text: 'Organisation erfüllt die technischen Voraussetzungen nicht. Connector ist nicht konfiguriert.',
      date: '2026-02-25T16:45:00Z',
      author: 'Admin User',
      type: CommentType.REJECTION
    }
  ],
  'ucr-5': []
};

let additionalComments: UseCaseParticipationComment[] = [];

export function getUseCaseRequests(): UseCaseParticipationRequest[] {
  const all = [...MOCK_USE_CASE_REQUESTS, ...additionalRequests];
  return all.map(r => {
    const override = stateChanges.get(r.id);
    return override ? { ...r, admissionState: override } : r;
  });
}

export function getUseCaseRequestById(id: string): UseCaseParticipationRequest | undefined {
  return getUseCaseRequests().find((r) => r.id === id);
}

export function getUseCaseComments(requestId: string): UseCaseParticipationComment[] {
  const base = MOCK_USE_CASE_COMMENTS[requestId] || [];
  const additional = additionalComments.filter(
    (c) => (c as any)['requestId'] === requestId
  );
  return [...base, ...additional];
}

export function addUseCaseComment(
  requestId: string,
  comment: UseCaseParticipationComment
): void {
  (comment as any)['requestId'] = requestId;
  additionalComments.push(comment);
}

const USE_CASE_NAMES: Record<string, string> = {
  'supply-chain': 'Supply Chain Digitalization',
  'shortage-management': 'Shortage Management',
  'product-passport': 'Carbon Footprint & Digital Product Passport'
};

export function createUseCaseRequest(useCaseId: string, organizationId: string, organizationLegalName: string, representativeName: string): UseCaseParticipationRequest {
  const request: UseCaseParticipationRequest = {
    id: `ucr-${Date.now()}`,
    organizationId,
    useCaseId,
    useCaseName: USE_CASE_NAMES[useCaseId] || useCaseId,
    organizationLegalName,
    representativeName,
    admissionState: AdmissionState.PENDING,
    createdDate: new Date().toISOString()
  };
  additionalRequests.push(request);
  persistAdditionalRequests();
  return request;
}

export function updateUseCaseRequestState(
  id: string,
  state: AdmissionState,
  reasoning: string
): UseCaseParticipationRequest | undefined {
  const request = MOCK_USE_CASE_REQUESTS.find((r) => r.id === id);
  if (request) {
    request.admissionState = state;
    stateChanges.set(id, state);
    persistStateChanges();
    const commentType =
      state === AdmissionState.APPROVED ? CommentType.APPROVAL : CommentType.REJECTION;
    addUseCaseComment(id, {
      id: `ucc-${Date.now()}`,
      text: reasoning,
      date: new Date().toISOString(),
      author: 'Current User',
      type: commentType
    });
  }
  return request;
}
